import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, RefreshCwIcon, DatabaseIcon, GaugeIcon, DownloadCloudIcon, ActivityIcon, BugIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WordPressOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress onderhoud | Veilig & up-to-date | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'WordPress onderhoud | Veilig & up-to-date | Noord',
      'og:description': 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/wordpress-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['pakketten', 'contact'];
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            const anchor = document.getElementById('anchor');
            if (anchor) {
              anchor.href = `#${sectionId}`;
            }
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe vaak worden WordPress updates uitgevoerd?',
    a: "Wij voeren wekelijks controles uit op beschikbare updates voor WordPress core, plugins en thema's. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere updates plannen wij zorgvuldig in om compatibiliteitsproblemen te voorkomen."
  }, {
    q: 'Wat gebeurt er als er een probleem optreedt?',
    a: 'Bij problemen grijpen wij direct in. Dankzij onze dagelijkse backups kunnen we jouw website snel herstellen naar een werkende versie. Daarnaast monitoren wij je website 24/7, zodat we problemen vaak al oplossen voordat jij ze merkt.'
  }, {
    q: 'Heb ik WordPress onderhoud echt nodig?',
    a: 'Ja. WordPress is open-source software die regelmatig updates ontvangt voor beveiliging en functionaliteit. Zonder onderhoud loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen met plugins. Professioneel WordPress onderhoud voorkomt deze problemen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-3125">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-3126">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3127">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-3128" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-3129" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-3130" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3131">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-3132">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-3133">
              Home
            </a>
            <span className="text-navy-500" data-id="element-3134">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-3135">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-3136">/</span>
            <span className="text-accent-light font-medium" data-id="element-3137">
              WordPress onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-3138">
            WordPress onderhoud zonder zorgen
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-3139">
            Wij houden je WordPress website veilig, snel en up-to-date. Zodat
            jij je kunt focussen op ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-3140">
            <a href="#pakketten" onClick={e => scrollToSection(e, 'pakketten')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-3141">
              Bekijk onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-3142" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud nodig is */}
      <section className="py-24 bg-surface-muted" data-id="element-3143">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3144">
          <div className="text-center mb-16" data-id="element-3145">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3146">
              Waarom WordPress onderhoud nodig is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3147">
              Een WordPress website zonder onderhoud is kwetsbaar. Voorkom
              problemen met een professionele WordPress onderhoud service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3148">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3149">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3150">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-3151" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3152">
                Security updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3153">
                WordPress beveiligingsupdates beschermen je website tegen
                hackers en malware. Zonder tijdige updates loop je onnodig
                risico.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3154">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3155">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-3156" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3157">
                Plugin updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3158">
                Verouderde plugins veroorzaken conflicten en beveiligingslekken.
                Wij houden al je plugins actueel en compatibel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3159">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3160">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-3161" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3162">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3163">
                Dagelijkse backups zorgen ervoor dat je website altijd te
                herstellen is, mocht er onverhoopt iets misgaan.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3164">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3165">
                <GaugeIcon className="w-6 h-6 text-accent" data-id="element-3166" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3167">
                Performance
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3168">
                Een snelle website scoort beter in Google en zorgt voor een
                betere gebruikerservaring. Wij optimaliseren continu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij doen */}
      <section id="pakketten" className="py-24 bg-white" data-id="element-3169">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3170">
          <div className="text-center mb-16" data-id="element-3171">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3172">
              Wat wij doen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3173">
              Ons WordPress onderhoud pakket dekt alles wat je nodig hebt om
              zorgeloos online te zijn.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3174">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3175">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3176">
                <RefreshCwIcon className="w-6 h-6 text-navy-700" data-id="element-3177" />
              </div>
              <div data-id="element-3178">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3179">
                  WordPress updates
                </h3>
                <p className="text-slate-600" data-id="element-3180">
                  Wekelijkse controle en uitvoering van WordPress core, plugin
                  en thema updates. Altijd getest op compatibiliteit voordat we
                  live gaan.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3181">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3182">
                <DownloadCloudIcon className="w-6 h-6 text-navy-700" data-id="element-3183" />
              </div>
              <div data-id="element-3184">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3185">
                  Backups
                </h3>
                <p className="text-slate-600" data-id="element-3186">
                  Dagelijkse automatische backups van je volledige website en
                  database. Extern opgeslagen zodat je data altijd veilig is.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3187">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3188">
                <ActivityIcon className="w-6 h-6 text-navy-700" data-id="element-3189" />
              </div>
              <div data-id="element-3190">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3191">
                  Monitoring
                </h3>
                <p className="text-slate-600" data-id="element-3192">
                  24/7 uptime monitoring van je website. Bij downtime ontvangen
                  wij direct een melding en grijpen we in.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3193">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3194">
                <BugIcon className="w-6 h-6 text-navy-700" data-id="element-3195" />
              </div>
              <div data-id="element-3196">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3197">
                  Technische support
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-3198">
                  Loop je ergens tegenaan? Wij helpen je snel weer op weg. Van
                  formulieren tot weergaveproblemen. Wij staan paraat.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-3199">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg group" data-id="element-3200">
              Vraag een onderhoudspakket aan
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-3201" />
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-3202">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3203">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-3204">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3205">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3206">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3207">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3208">
              Website hosting
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3209">•</span>
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3210">
              Website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-3211">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3212">
          <div className="text-center mb-16" data-id="element-3213">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3214">
              Veelgestelde vragen over WordPress onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-3215">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-3216">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3217">
                  <span className="font-bold text-navy-900" data-id="element-3218">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-3219" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-3220" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-3221">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3222" />
    </main>;
}